import { ref, shallowRef, watch, type Component } from 'vue'
import { defineStore } from 'pinia'
import { useTokenStore } from './useTokenStore'
import LoginView from '@/features/auth/ui/modals/LoginView.vue'
import SignUpView from '@/features/auth/ui/modals/SignUpView.vue'
import ForgotPasswordView from '@/features/auth/ui/modals/ForgotPasswordView.vue'
import CheckEmailView from '@/features/auth/ui/modals/CheckEmailView.vue'
import SetNewPasswordView from '@/features/auth/ui/modals/SetNewPasswordView.vue'
import LinkExpiredView from '@/features/auth/ui/modals/LinkExpiredView.vue'
import PasswordUpdatedView from '@/features/auth/ui/modals/PasswordUpdatedView.vue'
import VerifyUser from '@/features/auth/ui/modals/VerifyUser.vue'
import EndOfRegistration from '@/features/auth/ui/modals/EndOfRegistration.vue'

/**
 * The screens the auth modal can show, named after the redesign (Figma
 * `66:46881`). Sign in and sign up are separate screens here — the tabbed
 * wrapper is on its way out.
 */
const views = {
  login: LoginView,
  signUp: SignUpView,
  forgotPassword: ForgotPasswordView,
  checkEmail: CheckEmailView,
  setNewPassword: SetNewPasswordView,
  linkExpired: LinkExpiredView,
  passwordUpdated: PasswordUpdatedView,
  verifyUser: VerifyUser,
  endOfRegistration: EndOfRegistration,
} satisfies Record<string, Component>

export type AuthModalViewKey = keyof typeof views

/**
 * Pre-redesign names kept alive on purpose: `showAuthModal('signInSignUpWrapper')`
 * is called from 40+ unrelated files (home, product, saved, style-space, brands,
 * bottom nav, paywall) and asserted in `usePaywallStore.spec.ts`. Renaming at the
 * call sites is a separate cleanup — this layer keeps them all working.
 */
const aliases: Record<string, AuthModalViewKey> = {
  signInSignUpWrapper: 'login',
  signIn: 'login',
  resetPassword: 'forgotPassword',
  checkEmailResetPassword: 'checkEmail',
  resetPasswordSuccess: 'passwordUpdated',
}

export type AuthModalView = AuthModalViewKey | keyof typeof aliases

/** Exported so the mapping can be tested without mounting the modal. */
export function resolveAuthView(view: AuthModalView): Component {
  const key = (aliases[view as string] ?? view) as AuthModalViewKey
  return views[key] ?? views.login
}

export const useAuthModal = defineStore('authModal', () => {
  const authModal = ref({
    isShow: false,
    isError: false,
    isLoading: false,
    currentView: views.login as Component,
  })

  const changeViewHandler = (view: AuthModalView) => {
    authModal.value.currentView = resolveAuthView(view)
  }

  /**
   * T-016 — what the guest was doing when sign-in got in the way (open Style Space, start the
   * size quiz…). It runs once the session appears — sign in, sign up or a social popup — on
   * the same page; closing the modal without signing in drops it.
   */
  const pendingAfterAuth = shallowRef<(() => void) | null>(null)
  const tokenStore = useTokenStore()

  watch(
    () => tokenStore.isAuthenticated(),
    (signedIn) => {
      if (!signedIn || !pendingAfterAuth.value) return
      const next = pendingAfterAuth.value
      pendingAfterAuth.value = null
      next()
    },
  )

  const showAuthModal = (currentView: AuthModalView, options: { then?: () => void } = {}) => {
    if (options.then) pendingAfterAuth.value = options.then
    changeViewHandler(currentView)
    authModal.value.isShow = true
  }

  const hideAuthModal = () => {
    authModal.value.isShow = false
    // A successful sign-in closes the modal too — give its tokens a tick before deciding
    // the guest walked away.
    setTimeout(() => {
      if (!tokenStore.isAuthenticated()) pendingAfterAuth.value = null
    }, 0)
  }

  return { authModal, showAuthModal, hideAuthModal }
})

export const useAuthModalStore = useAuthModal
